import React, { useRef, useState } from "react";
import { KatCard, KatIcon } from "@amzn/katal-react";
import { Trans, useTranslation } from "react-i18next";
import type { LocalizedText, NestUseCase } from "src/api/interfaces/nest";
import "./index.css";

interface NestUploadCardProps {
  useCase: NestUseCase;
  documentTypeName: LocalizedText;
  onFilesSelected: (files: File[]) => void;
}

export function NestUploadCard({
  useCase,
  documentTypeName,
  onFilesSelected,
}: NestUploadCardProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { t } = useTranslation();

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    if (files.length) {
      onFilesSelected(files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const ALLOWED_EXTENSIONS = [".xml", ".edi", ".pdf"];

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files)
      .filter((f) =>
        ALLOWED_EXTENSIONS.some((ext) => f.name.toLowerCase().endsWith(ext))
      )
      .slice(0, 5);
    if (files.length) onFilesSelected(files);
  };

  return (
    <button
      type="button"
      className={`nest-upload-card${isDragging ? " nest-upload-card--dragging" : ""}`}
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="nest-upload-card__input"
        accept=".xml,.edi,.pdf"
        multiple
      />
      <KatCard className="nest-upload-card__card">
        <div className="nest-upload-card__content">
          <KatIcon name="cloud_upload" />
          <h2>
            {t("nest_upload_title", {
              name: t(useCase.name.key, useCase.name.parameters),
            })}
          </h2>

          <span className="nest-upload-card-doc-badge">
            <KatIcon size="small" name="description" />
            {t(documentTypeName.key, documentTypeName.parameters)}
          </span>

          <p>{t("nest_upload_subtitle")}</p>
          <footer>
            <Trans
              i18nKey="nest_upload_footer"
              components={{ bold: <strong /> }}
            />
          </footer>
        </div>
      </KatCard>
    </button>
  );
}
